/**
 * FirstRunGate — routes a new manager into the first-run setup flow (T30.2).
 *
 * Sits inside AuthGuard (a session must exist) and wraps the protected app
 * routes. After sign-in it reads the current household + the user's membership
 * role and, for a MANAGER whose household has no location yet, redirects to
 * `/onboarding`. Everyone else — a located household, or a member of an
 * existing household — falls straight through to the app.
 *
 * The decision itself is the pure `decideFirstRun` (firstRunGate.ts) so the
 * branch is unit-tested without a router. The gate only translates that
 * decision into a redirect, and only when the user is not already on the
 * onboarding route (the flow itself renders below the gate via the route
 * table, so the gate must let `/onboarding` through without bouncing).
 *
 * Reads are TanStack queries already used elsewhere (`useCurrentHousehold`,
 * `useHouseholds`) — no new state, no new endpoint. While they're in flight the
 * decision is `app` (no redirect), so the gate never bounces mid-hydration; it
 * re-evaluates when the queries settle.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  useCurrentHousehold,
  useHouseholds,
} from '@shopista/shared/api/hooks';
import { routes } from '../../routes/paths';
import { decideFirstRun } from './firstRunDecision';
import { isFirstRunDeferred } from './firstRunDeferral';
import { resolveCurrentMembership } from './currentMembership';

export interface FirstRunGateProps {
  children: ReactNode;
}

export function FirstRunGate({ children }: FirstRunGateProps): JSX.Element {
  const location = useLocation();
  const householdQuery = useCurrentHousehold();
  const householdsQuery = useHouseholds();

  // Already on the onboarding route — never redirect onto itself, and let the
  // flow render regardless of the decision (the manager is mid-setup).
  const onOnboarding = location.pathname.startsWith(routes.onboarding);

  const loading = householdQuery.isLoading || householdsQuery.isLoading;
  const latitude = householdQuery.data?.latitude;
  const currentMembership = resolveCurrentMembership(
    householdsQuery.data,
    householdQuery.data?.id,
  );

  const decision = decideFirstRun({
    loading,
    latitude,
    currentRole: currentMembership?.role,
    deferred: isFirstRunDeferred(householdQuery.data?.id),
  });

  if (decision === 'first-run' && !onOnboarding) {
    return <Navigate to={routes.onboarding} replace />;
  }

  return <>{children}</>;
}
